import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {TriggerQuery} from '@models/adminstration/machine/trigger/trigger-query/trigger-query';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {shareReplay} from 'rxjs/operators';
import {
  AddTriggerConfigurationRequest,
  TriggerConfiguration
} from '@models/adminstration/machine/trigger/trigger-configuration';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';

@Injectable({
  providedIn: 'root'
})
export class MachineGroupTriggerConfigurationService {

  private MACHINE_GROUP_TRIGGER_CONFIGURATION_LIST_URL_SUFFIX: string = '/api/web/admin/machinegroup/:id/triggerConfiguration';
  private MACHINE_GROUP_TRIGGER_CONFIGURATION_LIST_AVAILABLE_TRIGGER_QUERY_URL_SUFFIX: string = '/api/web/admin/machinegroup/:id/triggerConfiguration/listAvailableQueries?externalTriggerApi=false';
  private MACHINE_GROUP_TRIGGER_CONFIGURATION_ADD_URL_SUFFIX: string = '/api/web/admin/machinegroup/:id/triggerConfiguration';
  private MACHINE_GROUP_TRIGGER_CONFIGURATION_GAP_DETECTION_URL_SUFFIX: string = '/api/web/admin/machinegroup/:id/triggerConfiguration/gapDetection';
  private MACHINE_GROUP_TRIGGER_CONFIGURATION_DELETE_URL_SUFFIX: string = '/api/web/admin/machinegroup/:id/triggerConfiguration/:config';
  private MACHINE_GROUP_TRIGGER_CONFIGURATION_UPDATE_URL_SUFFIX: string = '/api/web/admin/machinegroup/:id/triggerConfiguration?configId=:config';
  private MACHINE_GROUP_TRIGGER_CONFIGURATION_ACTIVATE_URL_SUFFIX: string = '/api/web/admin/machinegroup/:id/triggerConfiguration/activateTriggerConfig/:config';
  private MACHINE_GROUP_TRIGGER_CONFIGURATION_DEACTIVATE_URL_SUFFIX: string = '/api/web/admin/machinegroup/:id/triggerConfiguration/deactivateTriggerConfig/:config';

  private readonly BASE_URL: string;

  constructor(
    private http: HttpClient,
    private apiLocation: ApiLocationService
  ) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public machineGroupAvailableTriggerQueries(machineGroupId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_GROUP_TRIGGER_CONFIGURATION_LIST_AVAILABLE_TRIGGER_QUERY_URL_SUFFIX;
    url = url.replace(':id', String(machineGroupId));
    return this.http.get<TriggerQuery[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  public machineGroupGapDetection(machineGroupId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_GROUP_TRIGGER_CONFIGURATION_GAP_DETECTION_URL_SUFFIX;
    url = url.replace(':id', String(machineGroupId));
    return this.http.get<number[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public machineGroupAddTriggerConfiguration(machineId: number, request: AddTriggerConfigurationRequest): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_GROUP_TRIGGER_CONFIGURATION_ADD_URL_SUFFIX;
    url = url.replace(':id', String(machineId));
    return this.http.post<TriggerConfiguration>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public machineGroupTriggerConfiguration(machineGroupId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_GROUP_TRIGGER_CONFIGURATION_LIST_URL_SUFFIX;
    url = url.replace(':id', String(machineGroupId));
    return this.http.get<TriggerConfiguration[]>(url, HTTPS_REQUEST_OPTIONS);
  }

  public machineGroupDeleteConfiguration(machineGroupId: number, triggerConfigurationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_GROUP_TRIGGER_CONFIGURATION_DELETE_URL_SUFFIX;
    url = url.replace(':id', String(machineGroupId)).replace(':config', String(triggerConfigurationId));

    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public machineGroupUpdateTriggerConfiguration(machineGroupId: number, request: TriggerConfiguration): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_GROUP_TRIGGER_CONFIGURATION_UPDATE_URL_SUFFIX;
    url = url.replace(':id', String(machineGroupId)).replace(':config', String(request.id));
    return this.http.post<TriggerConfiguration>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public machineGroupActivateTriggerConfig(parentId: number, triggerConfigurationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_GROUP_TRIGGER_CONFIGURATION_ACTIVATE_URL_SUFFIX;
    url = url.replace(':id', String(parentId)).replace(':config', String(triggerConfigurationId));
    const request = {
      config: triggerConfigurationId,
      id: parentId
    };
    return this.http.post<TriggerConfiguration>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public machineGroupDeactivateTriggerConfig(parentId: number, triggerConfigurationId: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_GROUP_TRIGGER_CONFIGURATION_DEACTIVATE_URL_SUFFIX;
    url = url.replace(':id', String(parentId)).replace(':config', String(triggerConfigurationId));
    const request = {
      config: triggerConfigurationId,
      id: parentId
    };
    return this.http.post<TriggerConfiguration>(url, request, HTTPS_REQUEST_OPTIONS);
  }
}
